import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import {
  hostSelector,
  invitationSelector,
  stripeCheckoutSelector,
  userSelector
} from 'src/selectors'
import MembershipCheckout from 'src/containers/StripeCheckoutContainer/MembershipCheckout/MembershipCheckout'
import CreditsCheckout from 'src/containers/StripeCheckoutContainer/CreditsCheckout/CreditsCheckout'
import UpgradePlanCheckout from 'src/containers/StripeCheckoutContainer/UpgradePlanCheckout/UpgradePlanCheckout'
import SendInvitesManually from 'src/containers/StripeCheckoutContainer/SendInvitesManually/SendInvitesManually'
import PendingResult from 'src/containers/StripeCheckoutContainer/PendingResult/PendingResult'
import CheckoutSuccess from 'src/containers/StripeCheckoutContainer/CheckoutSuccess/CheckoutSuccess'
import Init from 'src/containers/StripeCheckoutContainer/Init/Init'
import { PurchaseWorkflowNode } from 'src/actions/stripeCheckout'
import TestSubscription from '../TestSubscriptions/TestSubscription'
import { UpgradableSubscriptionTypes } from 'src/services/stripe_checkout_configs'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import Button from 'src/components/common/Button'
import { NoMembership } from '../NoMembership/NoMembership'
import PurchaseOnMobile from '../PurchaseOnMobile/PurchaseOnMobile'
import { isMobile } from '../../../services/browser_detection'
import CoHostNotice from '../CoHostNotice/CoHostNotice'

import styles from './purchase-plan-and-credits.scss'

const ENABLE_MOBILE_WEB_PURCHASE = false

const PurchasePlanAndCredits = ({ onClose }) => {
  const user = useSelector(userSelector) || {}
  const checkoutContext = useSelector(stripeCheckoutSelector)
  const invitation = useSelector(invitationSelector)
  const host = useSelector(hostSelector)
  const currentSub = user.current_subscription
  const defaultWorkflowNode = checkoutContext.defaultWorkflowNode

  const [currentWorkflowNode, setCurrentWorkflowNode] = useState(defaultWorkflowNode)

  const onGoBack = () => {
    setCurrentWorkflowNode(defaultWorkflowNode)
  }

  const showGoBack = defaultWorkflowNode !== currentWorkflowNode

  const onMobileNodes = [
    PurchaseWorkflowNode.PurchaseOnMobileApp,
    PurchaseWorkflowNode.PurchasePlanOnMobileApp,
    PurchaseWorkflowNode.PurchaseCredits
  ]

  if (isMobile() && !ENABLE_MOBILE_WEB_PURCHASE && !onMobileNodes.includes(currentWorkflowNode)) {
    setCurrentWorkflowNode(PurchaseWorkflowNode.PurchaseOnMobileApp)
  } else if (invitation?.cohost) {
    setCurrentWorkflowNode(PurchaseWorkflowNode.CoHostNotice)
  }

  switch (currentWorkflowNode) {
    case PurchaseWorkflowNode.PurchaseCredits:
      return (
        <CreditsCheckout
          user={user}
          currentSub={currentSub}
          onGoBack={onGoBack}
          goToCheckoutNode={node => setCurrentWorkflowNode(node)}
          showGoBack={showGoBack}
        />
      )
    case PurchaseWorkflowNode.UpgradePlan:
      const canSeeUpgradePlan = UpgradableSubscriptionTypes.includes(currentSub?.subscription_type)
      if (canSeeUpgradePlan) {
        return (
          <UpgradePlanCheckout
            user={user}
            currentSub={currentSub}
            onGoBack={onGoBack}
            goToCheckoutNode={node => setCurrentWorkflowNode(node)}
            onClose={onClose}
            showGoBack={showGoBack}
          />
        )
      } else {
        return (
          <main className={styles['can-not-upgrade-container']}>
            <div className={styles['message']}>
              <ExclamationCircleOutlined style={{ marginRight: 10 }} />
              You subscribed using a different device. Open Hobnob on your other device to manage
              your subscription.
            </div>
            <Button filled={false} className={styles['close-btn']} onClick={onClose}>
              Close
            </Button>
          </main>
        )
      }
    case PurchaseWorkflowNode.SendInviteManually:
      return <SendInvitesManually onGoBack={onGoBack} />
    case PurchaseWorkflowNode.PendingMembershipPaymentResult:
      return (
        <PendingResult
          goToCheckoutNode={node => setCurrentWorkflowNode(node)}
          nextNode={PurchaseWorkflowNode.MembershipCheckoutSuccess}
        />
      )
    case PurchaseWorkflowNode.PendingCreditsPaymentResult:
      return (
        <PendingResult
          goToCheckoutNode={node => setCurrentWorkflowNode(node)}
          nextNode={PurchaseWorkflowNode.CreditsCheckoutSuccess}
        />
      )
    case PurchaseWorkflowNode.MembershipCheckoutSuccess:
      return <CheckoutSuccess type="membership" onClose={onClose} />
    case PurchaseWorkflowNode.CreditsCheckoutSuccess:
      return <CheckoutSuccess type="credits" onClose={onClose} />
    case PurchaseWorkflowNode.PurchasePlan:
      return (
        <MembershipCheckout
          user={user}
          currentSub={currentSub}
          showGoBack={showGoBack}
          checkoutContext={checkoutContext}
          goToCheckoutNode={node => setCurrentWorkflowNode(node)}
          onGoBack={onGoBack}
          onClose={onClose}
        />
      )
    case PurchaseWorkflowNode.NoMembershipFromPurchasePlan:
      return (
        <NoMembership
          goToCheckoutNode={node => setCurrentWorkflowNode(node)}
          previousNode={PurchaseWorkflowNode.PurchasePlan}
        />
      )
    case PurchaseWorkflowNode.NoMembershipFromUpgradePlan:
      return (
        <NoMembership
          goToCheckoutNode={node => setCurrentWorkflowNode(node)}
          previousNode={PurchaseWorkflowNode.UpgradePlan}
        />
      )
    case PurchaseWorkflowNode.PurchaseOnMobileApp:
      return (
        <PurchaseOnMobile
          params={{ requiredCredits: checkoutContext.requiredCredits }}
          currentSub={currentSub}
        />
      )
    case PurchaseWorkflowNode.PurchasePlanOnMobileApp:
      return (
        <PurchaseOnMobile
          params={{ requiredCredits: checkoutContext.requiredCredits }}
          currentSub={currentSub}
          title="Subscribe"
        />
      )
    case PurchaseWorkflowNode.CoHostNotice:
      const hostname = host ? `${host.first_name} ${host.last_name}` : ''
      return <CoHostNotice params={{ hostname }} onClose={onClose} />
    case PurchaseWorkflowNode.TestSubs:
      return <TestSubscription />
    default:
      return (
        <Init
          user={user}
          currentSub={currentSub}
          params={{ requiredCredits: checkoutContext.requiredCredits }}
          goToCheckoutNode={node => setCurrentWorkflowNode(node)}
        />
      )
  }
}

PurchasePlanAndCredits.propTypes = {
  onClose: PropTypes.func
}

export default PurchasePlanAndCredits
